import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import storyBaseService, { StoryBaseCreateRequest } from '../service/story-base.service';
import { useNavigate } from 'react-router-dom';
import voiceService from '../service/voice.service';
import { VoiceEntity } from '../types/entities';
import { useAuth } from '../context/AuthContext';
import IconButton from '@mui/material/IconButton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import Avatar from '@mui/material/Avatar';
import FaceIcon from '@mui/icons-material/Face';
import Face2Icon from '@mui/icons-material/Face2';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

const CreateVideoPage: React.FC = () => {
  const [title, setTitle] = React.useState('');
  const [fullStory, setFullStory] = React.useState('');
  const [description, setDescription] = React.useState('You must not delete or alter the special characters in the story such as [surprised], [excited], and similar expressions.');
  const [language, setLanguage] = React.useState('tr');
  const [isDialog, setIsDialog] = React.useState(false);
  const [target, setTarget] = React.useState<'short' | 'youtube' | 'other'>('youtube');
  const [characters, setCharacters] = React.useState<Array<{ name: string; voiceId: string }>>([
    { name: '', voiceId: '' },
  ]);
  const [submitting, setSubmitting] = React.useState(false);
  const [preparing, setPreparing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [preparedFullStory, setPreparedFullStory] = React.useState<string | null>(null);
  const [hasPreparedOnce, setHasPreparedOnce] = React.useState<boolean>(false);
  const [usedPrepared, setUsedPrepared] = React.useState<boolean>(false);
  const [estimatedDurationMs, setEstimatedDurationMs] = React.useState<string>('');
  const navigate = useNavigate();
  const [voices, setVoices] = React.useState<VoiceEntity[]>([]);
  const [voicesLoading, setVoicesLoading] = React.useState<boolean>(false);
  const { user } = useAuth();
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [playingKey, setPlayingKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        setVoicesLoading(true);
        const data = await voiceService.getActiveVoices();
        if (!alive) return;
        setVoices(data || []);
      } catch (e) {
        // silent fail; will keep voice selects empty
      } finally {
        if (alive) setVoicesLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, []);

  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    };
  }, []);

  const speakerForIndex = (idx: number) => {
    const letters = ['A', 'B', 'C', 'D', 'E'];
    return `speaker ${letters[idx] || idx + 1}`;
  };

  const filledBorderSx = (filled: boolean) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: filled ? 'success.main' : undefined,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: filled ? 'success.main' : undefined,
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: filled ? 'success.main' : undefined,
    },
  });

  const parsePreparedDialog = (text: string) => {
    const src = String(text || '');
    const groups: Array<{ speaker: string; sep: string; text: string }> = [];
    // Find labels like "Speaker A:" or "speaker B;" anywhere in the text and keep the separator
    const labelRe = /(speaker\s*[ABCD])\s*([:;])\s*/gi;
    let match: RegExpExecArray | null;
    const matches: Array<{ speaker: string; sep: string; index: number; end: number }> = [];

    while ((match = labelRe.exec(src)) !== null) {
      matches.push({ speaker: match[1], sep: match[2], index: match.index, end: labelRe.lastIndex });
    }

    for (let i = 0; i < matches.length; i++) {
      const cur = matches[i];
      const next = matches[i + 1];
      const content = src.slice(cur.end, next ? next.index : undefined).trim();
      if (content) {
        groups.push({ speaker: cur.speaker, sep: cur.sep, text: content });
      }
    }

    return groups;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!hasPreparedOnce) {
      setError('Lütfen önce Prepare butonuna basın.');
      return;
    }
    if (!title || !fullStory || !language || !target) {
      setError('Lütfen tüm zorunlu alanları doldurun.');
      return;
    }
    if (!user?.id) {
      setError('Oturum bulunamadı.');
      return;
    }
    if (!characters.length) {
      setError('En az bir karakter eklemelisiniz.');
      return;
    }
    if (isDialog && characters.length > 5) {
      setError('En fazla 5 karakter eklenebilir.');
      return;
    }
    if (!isDialog && characters.length !== 1) {
      setError('Dialoğ kapalıyken yalnızca 1 karakter seçebilirsiniz.');
      return;
    }
    const invalid = characters.some((c) => !c.name || !c.voiceId);
    if (invalid) {
      setError('Tüm karakterler için isim ve voiceId zorunludur.');
      return;
    }

    const charactersJson: StoryBaseCreateRequest['charactersJson'] = characters.map((c, idx) => ({
      name: c.name,
      speaker: speakerForIndex(idx),
      voiceId: c.voiceId,
    }));

    const payload: StoryBaseCreateRequest = {
      title,
      fullStory,
      language,
      isDialog,
      target,
      charactersJson,
      memberId: user.id,
      description: description || null,
      estimatedDurationMs: estimatedDurationMs ? Number(estimatedDurationMs) : undefined,
    };

    try {
      setSubmitting(true);
      const created = await storyBaseService.createStoryBase(payload);
      navigate(`/templates/${created.id}`);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Oluşturma başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrepare = async () => {
    setError(null);
    setPreparedFullStory(null);
    setHasPreparedOnce(false);
    if (!fullStory) {
      setError('Lütfen Full Story alanını doldurun.');
      return;
    }
    const invalid = characters.some((c) => !c.name);
    if (invalid) {
      setError('Hazırlık için tüm karakterlerde isim olmalı.');
      return;
    }
    try {
      setPreparing(true);
      const preparePayload = {
        fullStory,
        isDialog,
        description: description || null,
        characters: characters.map((c, idx) => ({ speaker: speakerForIndex(idx), name: c.name })),
        language,
      };
      const res = await storyBaseService.prepareStory(preparePayload as any);
      setPreparedFullStory(res.fullStory || '');
      if (typeof res.estimatedDurationMs === 'number') {
        setEstimatedDurationMs(String(res.estimatedDurationMs));
      }
      setUsedPrepared(false);
      setHasPreparedOnce(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Hazırlık başarısız');
      setHasPreparedOnce(false);
    } finally {
      setPreparing(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          width: '75%',
          mx: 'auto',
          textAlign: 'center',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 1,
          // py: 2,
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>1. Step - Base Story</Typography>       
        <Backdrop
        open={preparing || submitting}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Stack spacing={1} alignItems="center">
          <CircularProgress color="inherit" />
          <Typography variant="body2">
            {(preparing && 'Preparing...') || (submitting && 'Creating...') || 'Loading...'}
          </Typography>
        </Stack>
      </Backdrop>
    </Box>
      <Box  sx={{mb: 2,  mx: 'auto',
          textAlign: 'center',}}>        
        <Typography variant="h4">Create Story</Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            sx={filledBorderSx(!!title)}
          />
          <TextField
            label="Full Story"
            value={fullStory}
            onChange={(e) => setFullStory(e.target.value)}
            required
            fullWidth
            multiline
            minRows={15}
            maxRows={15}
            sx={{ ...filledBorderSx(!!fullStory), '& textarea': { overflow: 'auto' } }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            sx={filledBorderSx(!!description)}
          />
          <TextField
            label="Estimated Duration (ms)"
            type="number"
            value={estimatedDurationMs}
            onChange={(e) => setEstimatedDurationMs(e.target.value)}
            fullWidth
            helperText={'AI bu hikayenin tahmini süresini "estimatedDurationMs" olarak belirledi. Değişiklik yapabilirsiniz.'}
            sx={filledBorderSx(!!estimatedDurationMs)}
          />
          {preparedFullStory && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2">Prepared Full Story</Typography>
                {(() => {
                  const groups = parsePreparedDialog(preparedFullStory);
                  const uniqueSpeakers = Array.from(new Set(groups.map((g) => g.speaker.trim().toLowerCase())));
                  const showMismatch = !isDialog && uniqueSpeakers.length > 1;
                  return showMismatch ? (
                    <Alert severity="warning">
                      İnceleme sonucunda birden fazla karakter tespit edildi. Bu karakterler için seçim yapın veya gelen cevabı kabul etmeden kendi hikaye metninizle devam edin.
                    </Alert>
                  ) : null;
                })()}
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {(() => {
                    const groups = parsePreparedDialog(preparedFullStory);
                    if (groups.length === 0) {
                      return (
                        <Box sx={{ whiteSpace: 'pre-wrap' }}>
                          <Typography variant="body2">{preparedFullStory}</Typography>
                        </Box>
                      );
                    }
                    return (
                      <Stack spacing={1.25}>
                        {groups.map((g, i) => (
                          <Typography key={i} variant="body2">
                            <strong>{g.speaker}{g.sep} </strong>
                            {g.text}
                          </Typography>
                        ))}
                      </Stack>
                    );
                  })()}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="contained" onClick={() => {
                    setFullStory(preparedFullStory);
                    setUsedPrepared(true);
                    const groups = parsePreparedDialog(preparedFullStory);
                    const uniqSpeakers = Array.from(new Set(groups.map((g) => g.speaker.trim().toLowerCase())));
                    if (uniqSpeakers.length > 1) {
                      if (!isDialog) setIsDialog(true);
                      if (characters.length < uniqSpeakers.length) {
                        const needed = uniqSpeakers.length - characters.length;
                        const extra = Array.from({ length: needed }, () => ({ name: '', voiceId: '' }));
                        setCharacters([...characters, ...extra]);
                      }
                    }
                  }}>
                    Use This
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setPreparedFullStory(null)}>
                    Clear
                  </Button>
                </Box>
              </Stack>
            </Paper>
          )}
          <FormControl fullWidth sx={filledBorderSx(!!language)}>
              <InputLabel id="lang-label">Language</InputLabel>
              <Select
                labelId="lang-label"
                label="Language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <MenuItem value="tr">TR - Turkish</MenuItem>
                <MenuItem value="en">EN - English</MenuItem>
                <MenuItem value="de">DE - German</MenuItem>
                <MenuItem value="es">ES - Spanish</MenuItem>
                <MenuItem value="ar">AR - Arabic</MenuItem>
                <MenuItem value="ru">RU - Russian</MenuItem>
                <MenuItem value="zh">ZH - Chinese</MenuItem>
                <MenuItem value="ko">KO - Korean</MenuItem>
                <MenuItem value="ja">JA - Japanese</MenuItem>
                <MenuItem value="fr">FR - French</MenuItem>
                <MenuItem value="pt">PT - Portuguese</MenuItem>
                <MenuItem value="it">IT - Italian</MenuItem>
              </Select>
            </FormControl>
          <FormControl fullWidth sx={filledBorderSx(!!target)}>
              <InputLabel id="target-label">Target</InputLabel>
              <Select
                labelId="target-label"
                label="Target"
                value={target}
                onChange={(e) => setTarget(e.target.value as any)}
              >
                <MenuItem value="short">short</MenuItem>
                <MenuItem value="youtube">youtube</MenuItem>
                <MenuItem value="other">other</MenuItem>
              </Select>
            </FormControl>
          <FormControlLabel
            control={<Switch checked={isDialog} onChange={(e) => setIsDialog(e.target.checked)} />}
            label="Is Dialog?"
          />

          <Divider />
          <Typography variant="subtitle1">Characters</Typography>
          <Typography variant="body2" color="text.secondary">
            {isDialog
              ? 'Dialog açık: En fazla 5 karakter ekleyebilirsiniz.'
              : 'Dialog kapalı: Sadece 1 karakter ekleyebilirsiniz.'}
          </Typography>

          <Stack spacing={1}>
            {characters.map((ch, idx) => (
              <Stack key={idx} spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  {speakerForIndex(idx)}
                </Typography>
                <TextField
                  label="Character Name"
                  value={ch.name}
                  onChange={(e) => {
                    const next = [...characters];
                    next[idx] = { ...next[idx], name: e.target.value };
                    setCharacters(next);
                  }}
                  required
                  sx={filledBorderSx(!!ch.name)}
                />
                <FormControl fullWidth sx={filledBorderSx(!!ch.voiceId)}>
                  <InputLabel id={`voice-label-${idx}`}>Voice</InputLabel>
                  <Select
                    labelId={`voice-label-${idx}`}
                    label="Voice"
                    value={ch.voiceId}
                    onChange={(e) => {
                      const next = [...characters];
                      next[idx] = { ...next[idx], voiceId: (e.target as any).value as string };
                      setCharacters(next);
                    }}
                    disabled={voicesLoading}
                  >
                    {voices.map((v) => {
                      const samples = (v.sampleAudioPath || '')
                        .split('&&&')
                        .map((s) => s.trim())
                        .filter(Boolean);
                      return (
                        <MenuItem key={v.id} value={v.externalVoiceId || ''} disabled={!v.externalVoiceId}>
                          <Stack spacing={0.5} sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>     
                              <Avatar
                                sx={{ width: 22, height: 22, fontSize: 14, bgcolor: v.gender === 'woman' ? 'secondary.main' : 'primary.main' }}
                              >
                                {v.gender === 'woman' ? (
                                  <Face2Icon fontSize="inherit" />
                                ) : (
                                  <FaceIcon fontSize="inherit" />
                                )}
                            </Avatar>
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {v.name}
                                {v.description ? ` — ${v.description}` : ''}
                                {!v.externalVoiceId ? ' (no externalVoiceId)' : ''}
                              </Typography>
                            </Box>
                            {samples.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {samples.map((url, i) => {
                                  const key = `${v.id}-${i}`;
                                  const isPlaying = playingKey === key;
                                  return (
                                    <IconButton
                                      key={i}
                                      size="small"
                                      color={isPlaying ? 'primary' : 'default'}
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        try {
                                          // If clicking the currently playing, stop it
                                          if (isPlaying) {
                                            if (audioRef.current) {
                                              audioRef.current.pause();
                                              audioRef.current.currentTime = 0;
                                              audioRef.current = null;
                                            }
                                            setPlayingKey(null);
                                            return;
                                          }

                                          // Stop any existing audio
                                          if (audioRef.current) {
                                            audioRef.current.pause();
                                            audioRef.current.currentTime = 0;
                                            audioRef.current = null;
                                          }

                                          const audio = new Audio(url);
                                          audioRef.current = audio;
                                          setPlayingKey(key);
                                          audio.onended = () => {
                                            setPlayingKey(null);
                                            if (audioRef.current === audio) {
                                              audioRef.current = null;
                                            }
                                          };
                                          audio.onerror = () => {
                                            setPlayingKey(null);
                                            if (audioRef.current === audio) {
                                              audioRef.current = null;
                                            }
                                          };
                                          audio.play().catch(() => {
                                            setPlayingKey(null);
                                          });
                                        } catch {
                                          setPlayingKey(null);
                                        }
                                      }}
                                      aria-label={isPlaying ? `Stop sample ${i + 1}` : `Play sample ${i + 1}`}
                                    >
                                      {isPlaying ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                                    </IconButton>
                                  );
                                })}
                              </Box>
                            )}
                          </Stack>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Box>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      const next = characters.filter((_, i) => i !== idx);
                      setCharacters(next.length ? next : [{ name: '', voiceId: '' }]);
                    }}
                    disabled={characters.length <= 1}
                  >
                    Remove Character
                  </Button>
                </Box>
                <Divider />
              </Stack>
            ))}
            <Box>
              <Button
                variant="outlined"
                onClick={() => {
                  if (isDialog) {
                    if (characters.length < 5) setCharacters([...characters, { name: '', voiceId: '' }]);
                  } else {
                    if (characters.length < 1) setCharacters([{ name: '', voiceId: '' }]);
                  }
                }}
                disabled={!isDialog ? characters.length >= 1 : characters.length >= 5}
              >
                Add Character
              </Button>
            </Box>
          </Stack>

          <Box>
            <Stack direction="column" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Check your story with its characters. This will sequence dialogues by speakers without changing your content, and detect any missing characters.
              </Typography>
              <Button
                variant="outlined"
                onClick={handlePrepare}
                disabled={preparing}
                sx={{
                  bgcolor: 'warning.main',
                  color: 'warning.contrastText',
                  borderColor: 'warning.dark',
                  '&:hover': {
                    bgcolor: 'warning.dark',
                    borderColor: 'warning.dark',
                  },
                  '&.Mui-disabled': {
                    bgcolor: (theme) => theme.palette.action.disabledBackground,
                    color: (theme) => theme.palette.action.disabled,
                    borderColor: (theme) => theme.palette.action.disabled,
                  },
                }}
              >
                {preparing ? 'Preparing...' : 'First Control'}
              </Button>
              {(() => {
                const currentGroups = parsePreparedDialog(fullStory);
                const uniqSpeakers = Array.from(new Set(currentGroups.map((g) => g.speaker.trim().toLowerCase())));
                const requiresMulti = uniqSpeakers.length > 1;
                const invalidSelection = usedPrepared && requiresMulti && (!isDialog || characters.length < uniqSpeakers.length);
                const disabled = submitting || preparing || !hasPreparedOnce || invalidSelection;
                return (
                  <Button type="submit" variant="contained" disabled={disabled}>
                    {submitting ? 'Creating...' : 'Create'}
                  </Button>
                );
              })()}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default CreateVideoPage;
